import { EmployeeThumb } from '@components';
import { useIsInternalUser } from '@hooks';
import React, { useEffect, useState } from 'react';
import { ChevronDown, User } from 'react-feather';
import { chartColors } from '../../components/charts/hardware/AssetAgeChart';
import StackedBarChart from '../../components/charts/stats/StackedBarChart';
import TextHeader from '../../components/headers/TextHeader';
import LoadingSprints from '../../components/loading/LoadingSprints';
import Tag from '../../components/tags/Tag';
import Typography from '../../components/typography/Typography';
import { useScrollContext } from '../../context/ScrollContextProvider';
import { useCompany } from '../../hooks/useCompany';
import { Ticket } from '../../interfaces/Ticket';
import { TicketService } from '../../services/tickets/Tickets';
import { FormatDate } from '../../utils/FormatDate';
import ListComponent from '../../utils/ListComponent';
import { TruncateString } from '../../utils/text/TruncateString';

type TicketsGroupedByType = Record<string, Ticket[]>;
interface TicketGroup {
  tickets: Ticket[];
  totalHours: number;
  color?: string;
}

const TicketYearOverviewContainer = ({
  data,
}: {
  data: { year: number; month: number; type?: string; graph?: any };
}) => {
  const ticketService = new TicketService();
  const { year, month, type } = data;
  const { companyId } = useCompany();
  const [tickets, setTickets] = useState<TicketsGroupedByType>({});
  const [chartData, setChartData] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { isScrolled } = useScrollContext();
  const [openTypes, setOpenTypes] = useState({});
  const internalUser = useIsInternalUser();

  const toggleType = (type: string) => {
    setOpenTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const tickets = await ticketService.getTickets(
          companyId,
          type || undefined,
          undefined,
          undefined,
          { year, month }
        );

        const grouped = sortTicketsByType(tickets);
        setTickets(grouped);

        const initialOpenState: Record<string, boolean> = {};
        Object.keys(grouped).forEach((type) => {
          initialOpenState[type] = true;
        });
        setOpenTypes(initialOpenState);
      } catch (error) {
        console.error('Error fetching tickets:', error);
      }
    };

    fetchTickets();
  }, [companyId, type, year, month]);

  const sortTicketsByType = (tickets: Ticket[]) => {
    const sorted: Record<string, TicketGroup> = {};
    const chartArray: { name: string; color: string; percentage: number }[] =
      [];

    tickets.forEach((ticket, index) => {
      const rawHours = ticket.tic_extra_info;
      const hours = rawHours
        ? parseFloat(String(rawHours).replace(',', '.'))
        : 0;

      if (!sorted[ticket.tic_type]) {
        sorted[ticket.tic_type] = { tickets: [], totalHours: 0 };
      }

      sorted[ticket.tic_type].tickets.push(ticket);
      sorted[ticket.tic_type].totalHours += hours;

      // Assign color once (first time we encounter the type)
      if (!sorted[ticket.tic_type].color) {
        sorted[ticket.tic_type].color = chartColors[index % chartColors.length];
      }
    });

    // Prepare chart data array
    Object.entries(sorted).forEach(([type, group], idx) => {
      chartArray.push({
        name: type,
        color: group.color || chartColors[idx],
        percentage: group.totalHours,
      });
    });

    setChartData(chartArray);

    return sorted;
  };

  const grandTotal = tickets
    ? Object.values(tickets).reduce((sum, group) => sum + group.totalHours, 0)
    : 0;

  return (
    <div className="w-full">
      <TextHeader title="Tickets" subtitle={`${month}/${year}`} />
      <Typography tag="p" type="label">
        Dit is een overzicht van tickets die aangemaakt zijn. Dit wil niet
        zeggen dat ieder ticket gefactureerd werd
      </Typography>

      <div
        className={`sticky top-4 py-4 z-50 py-2 my-2 ${'bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600'}`}
      >
        <StackedBarChart data={chartData} index={activeIndex} />
      </div>

      {tickets && Object.keys(tickets).length > 0 ? (
        <ListComponent
          data={Object.keys(tickets)}
          renderItem={(type, index) => {
            const group = tickets[type]; // group: { tickets, totalHours }

            return (
              <div className="my-4">
                <div
                  className="flex items-center justify-between cursor-pointer select-none"
                  onClick={() => toggleType(type)}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-5 h-3 rounded-xl"
                      style={{ backgroundColor: group.color }}
                    ></div>
                    <Typography tag="h3" type="subtitle">
                      {type}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2">
                    <Typography tag="span" type="bold">
                      {group.totalHours.toFixed(2)}u
                    </Typography>

                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        openTypes[type] ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openTypes[type]
                      ? 'max-h-[1000px] opacity-100 mt-4'
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <tbody>
                        <ListComponent
                          data={group.tickets}
                          renderItem={(t) => (
                            <tr className="">
                              {internalUser && (
                                <td className="px-1 py-3">
                                  <div className="flex items-center gap-3">
                                    <EmployeeThumb
                                      image={t.emp_foto}
                                      name={t.emp_naam}
                                    />
                                  </div>
                                </td>
                              )}
                              <td
                                className={`${internalUser && 'px-4'} py-3 text-gray-700 max-w-md truncate`}
                              >
                                <Typography tag="p" type="small">
                                  <TruncateString
                                    text={t.tic_omschrijving}
                                    limit={50}
                                  />
                                </Typography>
                                {t.tic_type_id && (
                                  <div className="flex items-center gap-2">
                                    <User size={14} />
                                    <Typography tag="span" type="small">
                                      {t.tic_type_id}
                                    </Typography>
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                <Typography tag="span" type="small">
                                  <FormatDate date={t.created_at} />
                                </Typography>
                              </td>

                              <td className="px-4 py-3">
                                <Tag
                                  label={t.tic_status}
                                  style={t.tic_status}
                                />
                              </td>

                              <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                                <Typography tag="span" type="small">
                                  {t.tic_extra_info}h
                                </Typography>
                              </td>
                            </tr>
                          )}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }}
        />
      ) : (
        <LoadingSprints />
      )}
      {tickets && Object.keys(tickets).length > 0 && (
        <div className="mt-6 text-right">
          <Typography tag="span" type="bold" className="text-gray-800">
            {grandTotal.toFixed(2).replace('.', ',')}u
          </Typography>
        </div>
      )}
    </div>
  );
};

export default TicketYearOverviewContainer;
